<script setup lang="ts">
import { ref, onMounted } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Item,
  Vendor,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDF,
  formatDate,
  isEmpty,
  printPDF,
  sharePDF,
  validatePurchaseGrid,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import { useScreenSize } from "../../../../utils/useScreenSize";
import ItemPurchaseGrid from "../../../../components/ui/Templates/ItemPurchaseGrid.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import { tokenManager } from "../../../../api/utils/tokenManager";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  copyItems?: boolean
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, routePath: string, title: string): void;
}>();

const { isMobile } = useScreenSize();
const isReadOnly = ref<boolean>(props.mode === "view");

const { fields, formData, setFieldRules, validateForm, setFormData, resetForm } = useFormValidation({
  purchase_orders_id: props.data?.purchase_orders_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  currency: props.data?.currency || tokenManager.getSetting("default_currency", true) || "MVR",
  exchange_rate: props.data?.exchange_rate || 1,
  currencys_id: props.data?.currencys_id || 1,
  vendor: props.data?.vendor,
  vendors_id: props.data?.vendors_id || "",
  prefix: props.data?.prefix || "",
  ref_no: props.data?.ref_no || "",
  warehouses_id: props.data?.warehouses_id || "",
  price_lists_id: props.data?.price_lists_id || "",
  credit_terms_id: props.data?.credit_terms_id || "",
  vendor_reference: props.data?.vendor_reference || "",
  remarks: props.data?.remarks || "",
  status_id: props.data?.status_id || "",
  uid: props.data?.uid || "",
  items: props.data?.items && !props.copyItems || ([] as Item[]),
  action: "",
  accounting: [],
  debit_note: null as any,
  goods_receipt: null as any,
  purchase_request: null as any,
  purchase_order: null as any,
});

const vendor = ref<Vendor>();
const warehouse = ref<Warehouse>();

setFieldRules("vendors_id", [required("Select Vendor")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
setFieldRules("price_lists_id", [required("Select Price List")]);

const handleWarehouse = (data: any) => {
  warehouse.value = data;
  prepareItemRow();
};

const itemGridRef = ref<InstanceType<typeof ItemPurchaseGrid> | null>(null);

const setData = async (data: any) => {
  setFormData(data);
  fields.value.date_time.value = formatDate(data.date_time);
  if (!props.copyItems)
    fields.value.items.value = data.items;
  else {
    await prepareItemRow()
    allowNewItemRow.value = true
    data.items.forEach((item: any) => {
      itemGridRef.value?.insertItem(item);
    });
    formData.value.credit_terms_id = 1
  }
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setData(response.data);
  }
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validatePurchaseGrid(formData.value.items, warehouse.value);
    if (!isValid) {
      MessageBox.show(
        "There are some Batch, Serial, Shelf or Variant Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    formData.value.action =
      download || share || print ? "download" : mail ? "mail" : "";
    //formData.value.download = download || share || print || mail
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show(props.title + " Saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDF(response.data.file);
      } else if (mail) {
        Toast.show(props.title + " mailed successfully");
      } else if (share) {
        sharePDF(
          response.data.file,
          vendor.value ? vendor.value.name : props.title + " from Faseyha POS",
          "viber"
        );
      } else if (print) {
        printPDF(response.data.file)
      } else {
        setData(response.data);
      }
    }
  }
};

const createConvertDocument = async (doc_type: string) => {
  const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "convert",
      {
        id: formData.value.uid,
        convert: doc_type
      }
    );
    if (response.success == true) {
      formData.value.debit_note = response.data.data
      MessageBox.confirm({
        title: doc_type + ' Created',
        message: doc_type + ' #(' + response.data.data.document_no + ') created successfully', 
        confirmText: 'Print ' + doc_type ,
        cancelText: "Close",
        extraButton: {
          text: "View " + doc_type ,
          action: () => {
            emit('viewDocument', formData.value.debit_note, 'Purchase Debit Note', 'procurement/purchases/purchase-debit-notes')
          }
        }
      }).then((confirmed: any) => {
        if (confirmed) {
          printPDF(response.data.file)
        }
      });
    }
}

const clearForm = () => {
  resetForm()
  fields.value.date_time.value = formatDate(new Date());
  console.log(fields.value.items)
  isReadOnly.value = false
};

const allowNewItemRow = ref(false);
const handleVendor = (data: any) => {
  vendor.value = data;
  prepareItemRow();
};

const currencySelected = (data: any) => {
  if (data) {
    formData.value.exchange_rate = data.exchange_rate || 1;
  } else {
    formData.value.exchange_rate = 1;
  }
};

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.vendors_id) &&
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.price_lists_id) &&
    !isEmpty(formData.value.credit_terms_id)
  ) {
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const activeTab = ref("items");

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Purchase Invoice# " + formData.value.ref_no);
};

const viewDocument = (doc_type: string) => {
  if (doc_type == 'debit_note' && formData.value.debit_note)
    emit('viewDocument', formData.value.debit_note, 'Purchase Debit Note', 'procurement/purchases/purchase-debit-notes')
  else if (doc_type == 'receipt' && formData.value.goods_receipt)
    emit('viewDocument', formData.value.goods_receipt, 'Purchase Goods Receipt', 'procurement/purchases/purchase-goods-receipts')
  else if (doc_type == 'request' && formData.value.purchase_request)
    emit('viewDocument', formData.value.purchase_request, 'Purchase Request', 'procurement/purchases/purchase-requests')
  else if (doc_type == 'order' && formData.value.purchase_order)
    emit('viewDocument', formData.value.purchase_order, 'Purchase Order', 'procurement/purchases/purchase-orders')
}

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";      
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});


</script>

<template>
  <ModalFormComponent
      width="100%"
      @close="$emit('close')"
    >
      <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
      <template #dropdownbuttons>
          <button
            @click="createConvertDocument('Purchase Debit Note')"
            v-if="!formData.debit_note && !formData.debit_note"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="FileInput" />
            Create Purchase Debit Note 
          </button>
          <button
            @click="viewDocument('debit_note')"
            v-if="formData.debit_note && formData.debit_note?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="FileInput" />
            View Debit Note ({{ formData.debit_note?.document_no }})
          </button>
          <button
            @click="viewDocument('receipt')"
            v-if="formData.goods_receipt && formData.goods_receipt?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="PackageCheck" />
            View Goods Receipt ({{ formData.goods_receipt?.document_no }})
          </button>
          <button
            @click="viewDocument('request')"
            v-if="formData.purchase_request && formData.purchase_request?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="ClipboardEdit" />
            View Purchase Request ({{ formData.purchase_request?.document_no }})
          </button>
          <button
            @click="viewDocument('order')"
            v-if="formData.purchase_order && formData.purchase_order?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="ClipboardEdit" />
            View Purchase Order ({{ formData.purchase_order?.document_no }})
          </button>
      </template>
    </DocumentHeader>
    </template>

      <!-- Main Content - Scrollable -->
      <template #form>
          <div class="grid md:grid-cols-2 gap-6 mb-8">
            <!-- Left Column -->
            <div class="space-y-6">
              <div>
                <div class="flex justify-between items-center mb-2">
                  <SearchSelect
                    label="Vendor"
                    end-point="procurement/master-data/vendors"
                    v-model="fields.vendors_id.value"
                    :readonly="isReadOnly"
                    :remote="true"
                    :withDetails="true"
                    :allow-new-record="true"
                    :error="
                      fields.vendors_id.touched ? fields.vendors_id.error : null
                    "
                    @blur="fields.vendors_id.touched = true"
                    @select="handleVendor"
                  />
                </div>

                <div
                  v-if="vendor"
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ vendor.name }}
                  </h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.email }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.mobile }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.address }}
                  </p>
                </div>
                <div
                  v-else
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
                >
                  No Vendor selected
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="space-y-6">
              <!--  Details -->
              <div class="space-y-4">
                <div class="grid md:grid-cols-3 gap-4">
                  <div>
                    <SearchSelect
                      label="Warehouse"
                      end-point="system/company/warehouses"
                      v-model="fields.warehouses_id.value"
                      :readonly="isReadOnly"
                      @select="handleWarehouse"
                      :error="
                        fields.warehouses_id.touched
                          ? fields.warehouses_id.error
                          : null
                      "
                      @blur="fields.warehouses_id.touched = true"
                    />
                  </div>
                  <div>
                    <SearchSelect
                      label="Price List"
                      end-point="finance/master-data/price-lists"
                      v-model="fields.price_lists_id.value"
                      :readonly="isReadOnly"
                      @select="prepareItemRow"
                      :error="
                        fields.price_lists_id.touched
                          ? fields.price_lists_id.error
                          : null
                      "
                      @blur="fields.price_lists_id.touched = true"
                    />
                  </div>
                  <div>
                    <SearchSelect
                      label="Payment Terms"
                      end-point="finance/master-data/credit-terms"
                      v-model="fields.credit_terms_id.value"
                      :readonly="isReadOnly"
                      @select="prepareItemRow"
                      :error="
                        fields.credit_terms_id.touched
                          ? fields.credit_terms_id.error
                          : null
                      "
                      @blur="fields.credit_terms_id.touched = true"
                    />
                  </div>
                </div>

                <div class="grid md:grid-cols-5 gap-4">
                    <SearchSelect
                      label="Currency"
                      end-point="finance/master-data/currency"
                      v-model="fields.currencys_id.value"
                      :readonly="isReadOnly"
                      @select="currencySelected"
                      :error="
                        fields.currencys_id.touched
                          ? fields.currencys_id.error
                          : null
                      "
                      @blur="fields.currencys_id.touched = true"
                    />
                    <NumberInput
                      label="Exchange Rate"
                      v-model="fields.exchange_rate.value"
                      :readonly="isReadOnly"
                      :error="
                        fields.exchange_rate.touched
                          ? fields.exchange_rate.error
                          : null
                      "
                      @blur="fields.exchange_rate.touched = true" />
                    <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Date
                    </label>
                    <input
                      v-model="formData.date_time"
                      :readonly="isReadOnly"
                      type="date"
                      class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Vendor Ref. Number
                    </label>
                    <input
                      v-model="fields.vendor_reference.value"
                      type="text"
                      :readonly="isReadOnly"
                      class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Document Number
                    </label>
                    <input
                      v-model="fields.ref_no.value"
                      type="text"
                      readonly
                      class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mb-6"
            v-if="!isMobile"
          >
            <TabComponent
              :tabs="[
                { name: 'items', label: 'Items', visible: true },
                {
                  name: 'accounting',
                  label: 'Accounting',
                  visible: isReadOnly,
                },
              ]"
              v-model="activeTab"
            >
              <template #items>
                <!-- Items Table -->
                <ItemPurchaseGrid
                  ref="itemGridRef"
                  :items="formData.items"
                  :readonly="isReadOnly"
                  :currency="formData.currency"
                  :exchange_rate="formData.exchange_rate"
                  :warehouses_id="formData.warehouses_id"
                  :exchange-rate="formData.exchange_rate"
                  :price_lists_id="formData.price_lists_id"
                  :allow-new-row="allowNewItemRow"
                  :shelf-managed="warehouse?.shelf_managed ?? false"
                  :shelfs="warehouse?.shelfs"
                  @items="updateItems"
                />
                <!-- Terms and Notes -->
                <div class="grid md:grid-cols-2 gap-6">
                <div>
                  <div>
                    <TextAreaInput
                      label="Remarks"
                      v-model="formData.remarks"
                      :readonly="isReadOnly"
                      :rows="7"
                      placeholder="Additional notes or remarks"
                    ></TextAreaInput>
                  </div>
                </div>
                <div>
                  <TextAreaInput
                    label="Internal Notes"
                    v-model="formData.remarks"
                    :rows="7"
                    :readonly="isReadOnly"
                    placeholder="Internal Notes"
                  />
                </div>
              </div>
              </template>
              <template #accounting>
                <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
              </template>
            </TabComponent>
          </div>
          <template v-if="isMobile">
              <ItemPurchaseGrid
                ref="itemGridRef"
                :items="formData.items"
                :readonly="isReadOnly"
                :currency="formData.currency"
                :exchange_rate="formData.exchange_rate"
                :vendor="vendor"
                :warehouses_id="formData.warehouses_id"
                :price_lists_id="formData.price_lists_id"
                :allow-change-price="false"
                :allow-discount="true"
                :allow-new-row="allowNewItemRow"
                :shelf-managed="warehouse?.shelf_managed ?? false"
                :shelfs="warehouse?.shelfs"
                @items="updateItems"
              />
              <h3 class="mb-2" v-if="isReadOnly">Accounting</h3>
              <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
            </template>
        </template>
      <!-- Footer -->
      <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
      </template>
    </ModalFormComponent>
</template>
